// Extended dataset used ONLY by the InteractiveWorldMap on the homepage.
// The 6 "core" countries have full site pages; the additional 14 are map-only
// destinations with their own highlights, flag, image and pin coordinates.

import { COUNTRIES, roundFlagUrl, type CountrySlug } from "./countries";
import { HIGHLIGHTS, type HighlightItem } from "./highlights";

export interface MapCountry {
  slug: string;              // used as React key + selection id
  name: string;
  shortName: string;
  code: string;              // ISO-2 lowercase for flag CDN
  mapX: number;              // 0-100 % on the map
  mapY: number;
  image: string;             // landmark cover image
  destinations: HighlightItem[];
  experiences: HighlightItem[];
  events: HighlightItem[];
  /** true if the slug corresponds to a full country hub route */
  hasGuide: boolean;
  /** country hub slug when hasGuide is true */
  countrySlug?: CountrySlug;
}

// ---- helpers ----------------------------------------------------------------

const img = (id: string) =>
  `/img/u/${id}.webp`;

// ---- 6 core countries (reuse existing data) --------------------------------

const CORE: MapCountry[] = COUNTRIES.map((c) => {
  const h = HIGHLIGHTS[c.slug];
  return {
    slug: c.slug,
    name: c.name,
    shortName: c.shortName,
    code: c.code,
    mapX: h.mapX,
    mapY: h.mapY,
    image: h.image,
    destinations: h.destinations,
    experiences: h.experiences,
    events: h.events,
    hasGuide: true,
    countrySlug: c.slug,
  };
});

// ---- 14 additional destinations --------------------------------------------

const EXTRA: MapCountry[] = [
  {
    slug: "germany",
    name: "Germany",
    shortName: "Germany",
    code: "de",
    mapX: 50,
    mapY: 32,
    image: img("photo-1560969184-10fe8719e047"),
    destinations: [
      { title: "Berlin", description: "Reinvented capital of art, music and startups.", meta: "Capital" },
      { title: "Munich", description: "Bavarian charm, beer gardens and Alps access.", meta: "Bavaria" },
      { title: "Hamburg", description: "Harbour city with warehouse-district cool.", meta: "North" },
    ],
    experiences: [
      { title: "Neuschwanstein Castle", description: "The fairytale castle in the Bavarian Alps." },
      { title: "Berlin Wall Trail", description: "Bike a 160 km loop tracing modern history." },
      { title: "Black Forest Drive", description: "Cuckoo clocks, spa towns and pine roads." },
    ],
    events: [
      { title: "Oktoberfest", description: "The world's largest folk festival.", meta: "September" },
      { title: "Berlinale", description: "One of the big-three European film festivals.", meta: "February" },
      { title: "Christmas Markets", description: "Glühwein and lights across every city.", meta: "December" },
    ],
    hasGuide: false,
  },
  {
    slug: "france",
    name: "France",
    shortName: "France",
    code: "fr",
    mapX: 48,
    mapY: 34,
    image: img("photo-1502602898657-3e91760cbb34"),
    destinations: [
      { title: "Paris", description: "Boulevards, museums and café mornings.", meta: "Île-de-France" },
      { title: "Provence", description: "Lavender fields and hilltop villages.", meta: "South" },
      { title: "French Riviera", description: "Nice, Cannes and Monaco on one coast.", meta: "Côte d'Azur" },
    ],
    experiences: [
      { title: "Louvre After Dark", description: "Wednesday & Friday late openings until 21:45." },
      { title: "Mont-Saint-Michel", description: "Tidal island abbey off the Normandy coast." },
      { title: "Loire Valley Châteaux", description: "Cycle between Renaissance castles." },
    ],
    events: [
      { title: "Cannes Film Festival", description: "The world's premier cinema showcase.", meta: "May" },
      { title: "Tour de France", description: "3 weeks of legendary road cycling.", meta: "July" },
      { title: "Bastille Day", description: "Fireworks over the Eiffel Tower.", meta: "14 July" },
    ],
    hasGuide: false,
  },
  {
    slug: "spain",
    name: "Spain",
    shortName: "Spain",
    code: "es",
    mapX: 46,
    mapY: 37,
    image: img("photo-1543783207-ec64e4d95325"),
    destinations: [
      { title: "Barcelona", description: "Gaudí architecture and Mediterranean beaches.", meta: "Catalonia" },
      { title: "Madrid", description: "World-class museums and late-night tapas.", meta: "Capital" },
      { title: "Seville", description: "Flamenco heart of sun-drenched Andalusia.", meta: "Andalusia" },
    ],
    experiences: [
      { title: "Alhambra of Granada", description: "Moorish palaces above the old town." },
      { title: "Camino de Santiago", description: "The historic pilgrimage across the north." },
      { title: "Ibiza Sunset Sail", description: "Boat around the Balearic west coast." },
    ],
    events: [
      { title: "La Tomatina", description: "The world's biggest tomato fight.", meta: "August" },
      { title: "San Fermín, Pamplona", description: "The running of the bulls.", meta: "July" },
      { title: "Primavera Sound", description: "Landmark music festival in Barcelona.", meta: "June" },
    ],
    hasGuide: false,
  },
  {
    slug: "netherlands",
    name: "Netherlands",
    shortName: "Netherlands",
    code: "nl",
    mapX: 49,
    mapY: 30,
    image: img("photo-1512470876302-972faa2aa9a4"),
    destinations: [
      { title: "Amsterdam", description: "Canal rings, museums and bike culture.", meta: "Capital" },
      { title: "Rotterdam", description: "Bold modern architecture and design.", meta: "South Holland" },
      { title: "Utrecht", description: "Medieval centre with a student pulse.", meta: "Central" },
    ],
    experiences: [
      { title: "Keukenhof Tulips", description: "7 million bulbs, 8 weeks a year." },
      { title: "Van Gogh Museum", description: "The largest collection of his works." },
      { title: "Zaanse Schans Windmills", description: "Working 18th-century windmills." },
    ],
    events: [
      { title: "King's Day", description: "The whole country turns orange.", meta: "27 April" },
      { title: "Amsterdam Dance Event", description: "World's biggest electronic music conference.", meta: "October" },
      { title: "Rotterdam Marathon", description: "One of the world's fastest courses.", meta: "April" },
    ],
    hasGuide: false,
  },
  {
    slug: "switzerland",
    name: "Switzerland",
    shortName: "Switzerland",
    code: "ch",
    mapX: 50,
    mapY: 34,
    image: img("photo-1530122037265-a5f1f91d3b99"),
    destinations: [
      { title: "Zurich", description: "Lakeside finance capital with alpine access.", meta: "German-Swiss" },
      { title: "Zermatt", description: "Car-free village beneath the Matterhorn.", meta: "Valais" },
      { title: "Interlaken", description: "Basecamp between two turquoise lakes.", meta: "Bernese" },
    ],
    experiences: [
      { title: "Glacier Express", description: "Panoramic 8-hour scenic train ride." },
      { title: "Jungfraujoch", description: "Europe's highest railway station." },
      { title: "Lake Geneva Cruise", description: "Vineyards, châteaux and Swiss Riviera." },
    ],
    events: [
      { title: "Montreux Jazz Festival", description: "Legendary lakeside music program.", meta: "July" },
      { title: "Fête de l'Escalade", description: "Geneva's historic torchlit festival.", meta: "December" },
      { title: "Basel Fasnacht", description: "UNESCO-listed carnival.", meta: "February" },
    ],
    hasGuide: false,
  },
  {
    slug: "ireland",
    name: "Ireland",
    shortName: "Ireland",
    code: "ie",
    mapX: 45,
    mapY: 30,
    image: img("photo-1590089415225-401ed6f9db8e"),
    destinations: [
      { title: "Dublin", description: "Literary capital with Georgian streets.", meta: "Leinster" },
      { title: "Galway", description: "Bohemian west-coast harbour town.", meta: "Connacht" },
      { title: "Cork", description: "Foodie hub near the Wild Atlantic Way.", meta: "Munster" },
    ],
    experiences: [
      { title: "Cliffs of Moher", description: "214 m sheer Atlantic cliffs." },
      { title: "Ring of Kerry", description: "179 km scenic driving loop." },
      { title: "Guinness Storehouse", description: "The country's most visited attraction." },
    ],
    events: [
      { title: "St Patrick's Festival", description: "5 days of parades in Dublin.", meta: "March" },
      { title: "Galway Arts Festival", description: "One of Europe's best summer arts events.", meta: "July" },
      { title: "Electric Picnic", description: "Ireland's flagship music festival.", meta: "September" },
    ],
    hasGuide: false,
  },
  {
    slug: "new-zealand",
    name: "New Zealand",
    shortName: "New Zealand",
    code: "nz",
    mapX: 92,
    mapY: 82,
    image: img("photo-1469854523086-cc02fe5d8800"),
    destinations: [
      { title: "Queenstown", description: "Adventure capital on Lake Wakatipu.", meta: "South Island" },
      { title: "Auckland", description: "City of sails and 48 volcanoes.", meta: "North Island" },
      { title: "Rotorua", description: "Māori culture and geothermal wonders.", meta: "Bay of Plenty" },
    ],
    experiences: [
      { title: "Milford Sound Cruise", description: "Fiordland's dramatic waterfalls." },
      { title: "Hobbiton Movie Set", description: "The Shire, still fully dressed." },
      { title: "Franz Josef Glacier", description: "Heli-hike onto ancient blue ice." },
    ],
    events: [
      { title: "World of WearableArt", description: "Iconic design & performance show.", meta: "September" },
      { title: "Waitangi Day", description: "The nation's founding-treaty day.", meta: "6 February" },
      { title: "Rhythm and Vines", description: "First-in-the-world New Year festival.", meta: "December" },
    ],
    hasGuide: false,
  },
  {
    slug: "japan",
    name: "Japan",
    shortName: "Japan",
    code: "jp",
    mapX: 84,
    mapY: 40,
    image: img("photo-1492571350019-22de08371fd3"),
    destinations: [
      { title: "Tokyo", description: "Neon districts and quiet garden temples.", meta: "Kanto" },
      { title: "Kyoto", description: "1,600 temples and preserved geisha streets.", meta: "Kansai" },
      { title: "Osaka", description: "Japan's kitchen, dotonbori nightlife.", meta: "Kansai" },
    ],
    experiences: [
      { title: "Mt Fuji Day Hike", description: "Climb Japan's most sacred peak in season." },
      { title: "Shinkansen Ride", description: "300 km/h bullet trains, ultra-punctual." },
      { title: "Onsen in Hakone", description: "Volcanic hot springs with Fuji views." },
    ],
    events: [
      { title: "Cherry Blossom Season", description: "Sakura viewing across the country.", meta: "Late March – April" },
      { title: "Gion Matsuri", description: "Kyoto's month-long festival.", meta: "July" },
      { title: "Sapporo Snow Festival", description: "Enormous ice & snow sculptures.", meta: "February" },
    ],
    hasGuide: false,
  },
  {
    slug: "south-korea",
    name: "South Korea",
    shortName: "S. Korea",
    code: "kr",
    mapX: 81,
    mapY: 40,
    image: img("photo-1517154421773-0529f29ea451"),

    destinations: [
      { title: "Seoul", description: "K-pop, palaces and 24-hour street food.", meta: "Capital" },
      { title: "Busan", description: "Coastal city of beaches and seafood.", meta: "South Coast" },
      { title: "Jeju Island", description: "Volcanic UNESCO island getaway.", meta: "South Sea" },
    ],
    experiences: [
      { title: "DMZ Tour", description: "See the world's most guarded border." },
      { title: "Gyeongbokgung Palace", description: "Joseon-era royal residence in Seoul." },
      { title: "Nami Island", description: "Tree-lined island of K-drama fame." },
    ],
    events: [
      { title: "Boryeong Mud Festival", description: "Two weeks of full-body mud play.", meta: "July" },
      { title: "Busan Film Festival", description: "Asia's biggest cinema event.", meta: "October" },
      { title: "Jinhae Cherry Blossoms", description: "Korea's most famous sakura tunnel.", meta: "April" },
    ],
    hasGuide: false,
  },
  {
    slug: "singapore",
    name: "Singapore",
    shortName: "Singapore",
    code: "sg",
    mapX: 76,
    mapY: 58,
    image: img("photo-1508964942454-1a56651d54ac"),
    destinations: [
      { title: "Marina Bay", description: "Iconic skyline and the Gardens by the Bay.", meta: "Central" },
      { title: "Sentosa Island", description: "Beaches, resorts and Universal Studios.", meta: "South" },
      { title: "Chinatown & Kampong Glam", description: "Layered heritage neighbourhoods.", meta: "Central" },
    ],
    experiences: [
      { title: "Hawker Centre Crawl", description: "Michelin-starred food for under $10." },
      { title: "Night Safari", description: "The world's first nocturnal wildlife park." },
      { title: "SkyPark Observation Deck", description: "200 m panorama atop Marina Bay Sands." },
    ],
    events: [
      { title: "F1 Night Grand Prix", description: "The only night race on the calendar.", meta: "September" },
      { title: "Chinese New Year", description: "Chingay Parade and river hongbao.", meta: "January / February" },
      { title: "Singapore Food Festival", description: "Two weeks celebrating local cuisine.", meta: "August" },
    ],
    hasGuide: false,
  },
  {
    slug: "uae",
    name: "United Arab Emirates",
    shortName: "UAE",
    code: "ae",
    mapX: 65,
    mapY: 47,
    image: img("photo-1512453979798-5ea266f8880c"),
    destinations: [
      { title: "Dubai", description: "Burj Khalifa, desert and Palm Jumeirah.", meta: "Gulf" },
      { title: "Abu Dhabi", description: "Grand Mosque and Louvre Abu Dhabi.", meta: "Capital" },
      { title: "Sharjah", description: "The cultural capital of the UAE.", meta: "Gulf" },
    ],
    experiences: [
      { title: "Desert Safari", description: "Dune bashing and Bedouin-style dinner." },
      { title: "Burj Khalifa Sunset", description: "The world's tallest observation deck." },
      { title: "Yas Island F1 Track", description: "Drive laps on the Grand Prix circuit." },
    ],
    events: [
      { title: "Dubai Shopping Festival", description: "6 weeks of retail and fireworks.", meta: "December – January" },
      { title: "Abu Dhabi F1 GP", description: "Season finale on Yas Marina Circuit.", meta: "November / December" },
      { title: "Dubai Air Show", description: "Major biennial aerospace event.", meta: "November" },
    ],
    hasGuide: false,
  },
  {
    slug: "brazil",
    name: "Brazil",
    shortName: "Brazil",
    code: "br",
    mapX: 33,
    mapY: 66,
    image: img("photo-1483729558449-99ef09a8c325"),
    destinations: [
      { title: "Rio de Janeiro", description: "Christ the Redeemer above Copacabana.", meta: "Southeast" },
      { title: "São Paulo", description: "Latin America's largest city.", meta: "Southeast" },
      { title: "Salvador", description: "Afro-Brazilian culture and colonial old town.", meta: "Bahia" },
    ],
    experiences: [
      { title: "Iguaçu Falls", description: "275 waterfalls on the Argentine border." },
      { title: "Amazon River Cruise", description: "Multi-day trip into the rainforest." },
      { title: "Pantanal Wildlife", description: "The world's largest tropical wetland." },
    ],
    events: [
      { title: "Rio Carnival", description: "The world's biggest party.", meta: "February / March" },
      { title: "Rock in Rio", description: "One of the largest music festivals on earth.", meta: "September" },
      { title: "Réveillon Copacabana", description: "2 million people ring in New Year.", meta: "31 December" },
    ],
    hasGuide: false,
  },
  {
    slug: "mexico",
    name: "Mexico",
    shortName: "Mexico",
    code: "mx",
    mapX: 18,
    mapY: 50,
    image: img("photo-1518638150340-f706e86654de"),
    destinations: [
      { title: "Mexico City", description: "Historic centre and world-class food scene.", meta: "Capital" },
      { title: "Tulum", description: "Mayan ruins meeting turquoise Caribbean.", meta: "Quintana Roo" },
      { title: "Oaxaca", description: "Mezcal, mole and indigenous craft.", meta: "South" },
    ],
    experiences: [
      { title: "Chichén Itzá", description: "One of the New 7 Wonders of the World." },
      { title: "Copper Canyon Train", description: "A network four times the size of the Grand Canyon." },
      { title: "Cenote Swim", description: "Freshwater sinkholes across the Yucatán." },
    ],
    events: [
      { title: "Día de los Muertos", description: "UNESCO-listed Day of the Dead.", meta: "1–2 November" },
      { title: "Guelaguetza", description: "Oaxaca's indigenous dance festival.", meta: "July" },
      { title: "Vive Latino", description: "Latin America's biggest rock festival.", meta: "March" },
    ],
    hasGuide: false,
  },
  {
    slug: "south-africa",
    name: "South Africa",
    shortName: "S. Africa",
    code: "za",
    mapX: 54,
    mapY: 74,
    image: img("photo-1580060839134-75a5edca2e99"),
    destinations: [
      { title: "Cape Town", description: "Table Mountain, wine lands and two oceans.", meta: "Western Cape" },
      { title: "Kruger National Park", description: "The Big Five in an area the size of Wales.", meta: "Mpumalanga" },
      { title: "Johannesburg", description: "Economic hub and gateway to Soweto history.", meta: "Gauteng" },
    ],
    experiences: [
      { title: "Garden Route Drive", description: "300 km of coast, forest and lagoons." },
      { title: "Robben Island", description: "Where Mandela was imprisoned for 18 years." },
      { title: "Shark Cage Diving", description: "Great whites off Gansbaai." },
    ],
    events: [
      { title: "Cape Town Jazz Festival", description: "Africa's biggest jazz gathering.", meta: "March / April" },
      { title: "AfrikaBurn", description: "Sister event to Burning Man in the Karoo.", meta: "April / May" },
      { title: "Comrades Marathon", description: "The world's largest ultra-marathon.", meta: "June" },
    ],
    hasGuide: false,
  },
];

export const MAP_COUNTRIES: MapCountry[] = [...CORE, ...EXTRA];

// Re-export helper so consumers don't need a second import
export { roundFlagUrl };
